import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(null);

/**
 * Shared session for the SPA.
 * Node JWT cookie via /api/auth/me and legacy alias /api/api_session.php
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let r = await fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' });
      if (r.status === 404) {
        r = await fetch('/api/api_session.php', { credentials: 'include', cache: 'no-store' });
      }
      if (!r.ok) {
        throw new Error(r.status === 401 || r.status === 403 ? 'unauth' : `session ${r.status}`);
      }
      const d = await r.json();
      if (!d || (typeof d === 'object' && d.error && !d.username && !d.authenticated)) {
        throw new Error(d?.error || 'unauth');
      }
      if (d.authenticated === false) {
        throw new Error('unauth');
      }
      setUser({
        authenticated: true,
        username: d.username || '',
        role: d.role || 'user',
        full_name: d.full_name || null,
        user_id: d.user_id ?? d.id ?? null,
      });
      setError(null);
      return d;
    } catch (e) {
      setUser(null);
      setError(e?.message || 'session failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (_) { /* ignore */ }
    setUser(null);
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const value = useMemo(
    () => ({ user, loading, error, refresh: loadSession, logout }),
    [user, loading, error, loadSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return { user: null, loading: false, error: 'no-auth-provider', refresh: () => {}, logout: async () => {} };
  }
  return ctx;
}
